import { t } from '@/i18n';
import { type AmountUnit, formatNumber, formatValueByUnit } from '@/utils';
import type { sheetTypeEnum } from '@/utils/enum';
import type { z } from 'zod';
import type { PaymentType } from '../../projects/_models/payment';
import {
  ContractMethodLabelModifier,
  type contractMethod,
} from '../../projects/_models/payment-contract';
import type { PaymentGroupsModel } from '../../projects/_models/payment-group';

/**
 * NOTE:
 * The migration data does not have data for payment graph.
 * And this system started running from FY2024.
 * So, we need to check if the fiscal year is 2024 or later.
 */
export const shouldShowPaymentGraph = (
  fiscalYear: number,
  sheetType: z.infer<typeof sheetTypeEnum>,
) => {
  return fiscalYear >= 2024 && sheetType !== 'KS';
};

export const getContractAmountOrExpendituresTitle = (type: PaymentType) => {
  return type === 'payee' ? t('amount-of-expenditures') : t('contract-amount');
};

const enableContractMethodDescription = (
  method: z.infer<typeof contractMethod> | null | undefined,
) => {
  return method === 'others' || method === 'negotiated-contract-others';
};

export const getContractName = (
  method: z.infer<typeof contractMethod> | null | undefined,
  contractMethodDescription: string | null | undefined,
) => {
  if (!method) {
    return null;
  }
  const label = ContractMethodLabelModifier[method];
  if (!enableContractMethodDescription(method) || !contractMethodDescription) {
    return label;
  }
  return `${label}(${contractMethodDescription})`;
};

export const getPaymentAmountBreakdownTableData = (
  data: z.infer<typeof PaymentGroupsModel>,
) => {
  return data.flatMap((paymentGroup) => {
    // The payments and contracts are sorted by amount when they are received from the API.
    const topPayments = paymentGroup.payments.filter(
      (payment) =>
        payment.totalContractAmount ===
        paymentGroup.payments[0]?.totalContractAmount,
    );
    return topPayments.flatMap((payment, paymentIndex) => {
      const topContracts = payment.contracts.filter(
        (contract) => contract.amount === payment.contracts[0]?.amount,
      );
      return topContracts.flatMap((contract, contractIndex) => {
        const contractCode = `${paymentGroup.displayCode}-${
          paymentIndex + 1
        }-${contractIndex + 1}`;
        const [firstAmountBreakdown, ...restAmountBreakdown] =
          contract.amountBreakdown ?? [];
        // If there is no expense item or use, do not indicate by payee or contract
        return firstAmountBreakdown
          ? [
              {
                amountBreakdownId: `${contractCode}-0`,
                displayCode: paymentGroup.displayCode,
                paymentName: payment.name,
                paymentId: payment.id,
                paymentGroupId: payment.paymentGroupId,
                contractOverview: contract.overview,
                ...firstAmountBreakdown,
                children:
                  restAmountBreakdown.length > 0
                    ? restAmountBreakdown.map((item, index) => ({
                        ...item,
                        amountBreakdownId: `${contractCode}-${index + 1}`,
                        paymentGroupId: payment.paymentGroupId,
                        paymentId: payment.id,
                      }))
                    : null,
              },
            ]
          : [];
      });
    });
  });
};

export const paymentValueConverter = (
  value: number | null | undefined,
  unit: AmountUnit,
  isDebugMode: boolean,
  negativeCount: number | null | undefined,
) =>
  formatValueByUnit(value, {
    operation: 'round',
    unit,
    isDebugMode,
    negativeCount,
  });

export const paymentNumberConverter = (value: number | null | undefined) =>
  formatNumber(value);
